import { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { MultiDayAppointmentRow } from '../types/agenda.types';
import { getStatusInfo } from '../utils/statusUtils';
import './MultiDayListView.scss';

interface MultiDayListTableProps {
  rows: MultiDayAppointmentRow[];
  loading: boolean;
}

/**
 * Lista de turnos de varios días (Semana y Mes). El dataset llega completo (sin paginación), así que los filtros de
 * la cabecera filtran en memoria.
 */
export default function MultiDayListTable({ rows, loading }: MultiDayListTableProps) {
  const [dayFilter, setDayFilter] = useState<string | null>(null);
  const [hourFilter, setHourFilter] = useState<string | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<string | null>(null);

  const dayOptions = useMemo(() => {
    const unique = new Set<string>();
    for (const r of rows) {
      if (r.dayLabel) unique.add(r.dayLabel);
    }
    return Array.from(unique).map((d) => ({ label: d, value: d }));
  }, [rows]);

  const hourOptions = useMemo(() => {
    const unique = new Set<string>();
    for (const r of rows) {
      if (r.time) unique.add(r.time.substring(0, 2));
    }
    return Array.from(unique)
      .sort()
      .map((h) => ({ label: `${h}:00`, value: h }));
  }, [rows]);

  const stateOptions = useMemo(() => {
    const unique = new Set<string>();
    for (const r of rows) {
      if (r.statusLabel) unique.add(r.statusLabel);
    }
    return Array.from(unique).map((s) => ({ label: s, value: s }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (dayFilter && row.dayLabel !== dayFilter) return false;
      if (hourFilter && row.time.substring(0, 2) !== hourFilter) return false;
      if (patientSearch && !row.patientName.toLowerCase().includes(patientSearch.toLowerCase())) return false;
      if (stateFilter && row.statusLabel !== stateFilter) return false;
      return true;
    });
  }, [rows, dayFilter, hourFilter, patientSearch, stateFilter]);

  const clearFilters = () => {
    setDayFilter(null);
    setHourFilter(null);
    setPatientSearch('');
    setStateFilter(null);
  };

  if (loading) {
    return (
      <div className="multiday-list multiday-list--loading">
        <div className="nv-loading" role="status">
          <i className="pi pi-spinner pi-spin" aria-hidden /> Cargando…
        </div>
      </div>
    );
  }

  const hasFilters = !!(dayFilter || hourFilter || patientSearch || stateFilter);
  const emptyMessage = hasFilters
    ? <>No hay resultados para estos filtros. <Button type="button" link label="Limpiar filtros" onClick={clearFilters} /></>
    : 'Todavía no hay turnos cargados.';

  const applied = (active: boolean) => (active ? 'nv-filter-applied' : undefined);

  const dropdownFilter = (
    value: string | null,
    options: { label: string; value: string }[],
    onChange: (value: string | null) => void,
    placeholder: string,
    ariaLabel: string,
  ) => (
    <Dropdown
      value={value}
      options={options}
      onChange={(e) => onChange(e.value ?? null)}
      placeholder={placeholder}
      showClear
      emptyMessage="Sin resultados"
      aria-label={ariaLabel}
      panelClassName="nv-scope"
    />
  );

  return (
    <div className="multiday-list">
      <DataTable
        value={filteredRows}
        scrollable
        scrollHeight="calc(100vh - 420px)"
        filterDisplay="row"
        emptyMessage={emptyMessage}
      >
        <Column
          header="Día"
          align="center"
          body={(row: MultiDayAppointmentRow) => row.dayLabel || '—'}
          filter
          filterField="dayLabel"
          showFilterMenu={false}
          filterElement={() => dropdownFilter(dayFilter, dayOptions, setDayFilter, 'Todos', 'Filtrar por día')}
          filterHeaderClassName={applied(!!dayFilter)}
        />
        <Column
          header="Hora"
          align="center"
          body={(row: MultiDayAppointmentRow) => <span className="nv-strong multiday-list__num">{row.time || '—'}</span>}
          filter
          filterField="time"
          showFilterMenu={false}
          filterElement={() => dropdownFilter(hourFilter, hourOptions, setHourFilter, 'Todas', 'Filtrar por hora')}
          filterHeaderClassName={applied(!!hourFilter)}
        />
        <Column
          header="Paciente"
          body={(row: MultiDayAppointmentRow) => row.patientName || '—'}
          filter
          filterField="patientName"
          showFilterMenu={false}
          filterElement={() => (
            <span className="p-input-icon-left">
              <i className="pi pi-search" aria-hidden />
              <InputText
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Buscar paciente"
                aria-label="Buscar paciente"
              />
            </span>
          )}
          filterHeaderClassName={applied(!!patientSearch)}
        />
        <Column
          header="Estado"
          body={(row: MultiDayAppointmentRow) => (
            row.statusLabel ? <Tag className={getStatusInfo(row.stateId).tagClass} value={row.statusLabel} /> : '—'
          )}
          filter
          filterField="statusLabel"
          showFilterMenu={false}
          filterElement={() => dropdownFilter(stateFilter, stateOptions, setStateFilter, 'Todos', 'Filtrar por estado')}
          filterHeaderClassName={applied(!!stateFilter)}
        />
        <Column header="Profesional / Agenda" body={(row: MultiDayAppointmentRow) => row.professional || '—'} />
        <Column
          header={<span className="nv-sr-only">Acciones</span>}
          className="nv-col-actions"
          align="right"
          alignHeader="right"
          filter
          filterField="_actions"
          showFilterMenu={false}
          filterElement={() => (
            <Button
              type="button"
              rounded
              className="nv-control nv-tip"
              icon="pi pi-eraser"
              aria-label="Limpiar filtros"
              data-pr-tooltip="Limpiar filtros"
              onClick={clearFilters}
            />
          )}
        />
      </DataTable>
    </div>
  );
}
